import { useEffect, useRef, useState, type RefObject } from 'react'
import { cleanMessage } from '@/components/shared/logs-explorer/utils/logs-to-text'
import type { Log } from '@/graphql.types'

type UseLogSelectionParams = {
  containerRef: RefObject<HTMLElement | null>
  logs: Log[]
  rowIndexOffset: number
}

const getRowIndex = (node: Node | null, container: HTMLElement) => {
  if (!node) {
    return null
  }
  const element = node instanceof Element ? node : node.parentElement
  const row = element?.closest<HTMLElement>('[data-index]')
  if (!row || !container.contains(row)) {
    return null
  }
  const index = Number(row.dataset.index)
  return Number.isNaN(index) ? null : index
}

const getSelectedMessageText = ({
  row,
  selectionRange,
  edge,
}: {
  row: Element | null
  selectionRange: Range
  edge: 'start' | 'end'
}) => {
  const message = row?.querySelector('[data-log-message]')
  if (!message) {
    return null
  }
  const messageRange = document.createRange()
  messageRange.selectNodeContents(message)
  const isEdgeInsideMessage =
    edge === 'start'
      ? selectionRange.compareBoundaryPoints(
          Range.START_TO_START,
          messageRange
        ) > 0
      : selectionRange.compareBoundaryPoints(Range.END_TO_END, messageRange) < 0
  if (!isEdgeInsideMessage) {
    return null
  }
  if (edge === 'start') {
    messageRange.setStart(
      selectionRange.startContainer,
      selectionRange.startOffset
    )
  } else {
    messageRange.setEnd(selectionRange.endContainer, selectionRange.endOffset)
  }
  return messageRange.toString()
}

const toPinnedIndexes = (
  anchorIndex: number | null,
  focusIndex: number | null
) =>
  [...new Set([anchorIndex, focusIndex])]
    .filter((index): index is number => index !== null)
    .sort((a, b) => a - b)

const areSameIndexes = (a: number[], b: number[]) =>
  a.length === b.length && a.every((value, index) => value === b[index])

export const useLogSelection = ({
  containerRef,
  logs,
  rowIndexOffset,
}: UseLogSelectionParams) => {
  const anchorIndexRef = useRef<number | null>(null)
  const focusIndexRef = useRef<number | null>(null)
  const [pinnedIndexes, setPinnedIndexes] = useState<number[]>([])

  useEffect(() => {
    const updatePinnedIndexes = () => {
      const next = toPinnedIndexes(
        anchorIndexRef.current,
        focusIndexRef.current
      )
      setPinnedIndexes((prev) => (areSameIndexes(prev, next) ? prev : next))
    }

    const handleSelectionChange = () => {
      const container = containerRef.current
      const selection = window.getSelection()
      if (
        !container ||
        !selection ||
        !selection.containsNode(container, true)
      ) {
        anchorIndexRef.current = null
        focusIndexRef.current = null
        updatePinnedIndexes()
        return
      }
      const anchorIndex = getRowIndex(selection.anchorNode, container)
      const focusIndex = getRowIndex(selection.focusNode, container)
      if (anchorIndex !== null) {
        anchorIndexRef.current = anchorIndex
      }
      if (focusIndex !== null) {
        focusIndexRef.current = focusIndex
      }
      updatePinnedIndexes()
    }

    const handleCopy = (event: ClipboardEvent) => {
      const container = containerRef.current
      const selection = window.getSelection()
      const anchorIndex = anchorIndexRef.current
      const focusIndex = focusIndexRef.current
      const isMultiRowSelection =
        anchorIndex !== null &&
        focusIndex !== null &&
        anchorIndex !== focusIndex
      if (
        !event.clipboardData ||
        !isMultiRowSelection ||
        !container ||
        !selection ||
        !selection.containsNode(container, true)
      ) {
        return
      }
      const firstRowIndex = Math.min(anchorIndex, focusIndex)
      const lastRowIndex = Math.max(anchorIndex, focusIndex)
      const firstLogIndex = Math.max(firstRowIndex - rowIndexOffset, 0)
      const lastLogIndex = lastRowIndex - rowIndexOffset
      const selectedLogs = logs.slice(firstLogIndex, lastLogIndex + 1)
      if (selectedLogs.length === 0) {
        return
      }
      const selectionRange =
        selection.rangeCount > 0 ? selection.getRangeAt(0) : null
      const getRow = (index: number) =>
        container.querySelector(`[data-index="${index}"]`)
      const lines = selectedLogs.map((log, index) => {
        const isFirst = index === 0
        const isLast = index === selectedLogs.length - 1
        const partialText =
          selectionRange && (isFirst || isLast)
            ? getSelectedMessageText({
                row: getRow(isFirst ? firstRowIndex : lastRowIndex),
                selectionRange,
                edge: isFirst ? 'start' : 'end',
              })
            : null
        return partialText ?? cleanMessage(log.message ?? '')
      })
      event.clipboardData.setData('text/plain', lines.join('\n'))
      event.preventDefault()
    }

    document.addEventListener('selectionchange', handleSelectionChange)
    document.addEventListener('copy', handleCopy)
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
      document.removeEventListener('copy', handleCopy)
    }
  }, [containerRef, logs, rowIndexOffset])

  return { pinnedIndexes }
}
